import { Api } from './api';

import type { 
  BusinessSales, 
  BusinessSalesReport, 
  CustomerTransactionQuery,
  CustomerDepositQuery,
  CustomerSalesReportQuery,
  IBank,
  IBusiness, 
  IBusinessUser, 
  ICustomer, 
  IDeposit, 
  IPage, 
  IProduct,
  ITransaction, 
  SignupData 
} from '../types';

export class CustomerApi extends Api {

  public static async signup(data: SignupData) {
    await this.post('/customer/signup', data);
  }

  public static async usernameExists(userName: string) {
    return this.post<{ exists: boolean }>('/customer/username/exists', { userName });
  }

  public static async documentNumberExists(documentType: 'id' | 'passport', documentNumber: string) {
    return this.post<{ exists: boolean }>('/customer/document-number/exists', { documentType, documentNumber });
  }

  public static async resetPassword(id: number) {
    return this.post<ICustomer>(`/customer/${id}/reset-password`);
  }

  public static async login(username: string, password: string) {
    return this.post('/customer/login', { username, password });
  }

  public static async getAuth() {
    return this.get<ICustomer>('/customer/auth');
  }

  public static async validateToken(token: string) {
    return this.get<ICustomer>(`/customer/validate-token/${token}`);
  }

  public static async changePassword(token: string, password: string) {
    await this.post('/customer/change-password', { token, password });
  }

  public static async getBusiness(page?: number, size?: number) {
    return this.get<IPage<IBusiness>>('/customer/business', { params: { page, size } });
  }

  public static async getBusinessById(id: number) {
    return this.get<IBusiness>(`/customer/business/${id}`);
  }

  public static async getBusinessUsers(id: number) {
    return this.get<IBusinessUser[]>(`/customer/business/${id}/users`);
  }

  public static async getBusinessSales(id: number, params: { startDate: string; endDate: string; period: 'day' | 'month' }) {
    return this.get<BusinessSales>(`/customer/business/${id}/sales`, { params });
  }

  public static async getSalesSummary(params: { startDate: string; endDate: string; period: 'day' | 'month' }) {
    return this.get<BusinessSales>('/customer/sales', { params });
  }

  public static async getBusinessTransactions(
    id: number, 
    params: CustomerTransactionQuery
  ) {
    return this.get<IPage<ITransaction>>(`/customer/business/${id}/transactions`, { params });
  }

  public static async getBusinessProduct(id: number) {
    return this.get<IProduct[]>(`/customer/business/${id}/products`);
  }

  public static async exportBusinessTransactions(id: number, startDate?: string, endDate?: string) {
    return this.api.get<Blob>(`/customer/business/${id}/export-transactions`, {
      responseType: 'blob',
      params: { startDate, endDate }
    });
  }

  public static async getBusinessBanks(id: number) {
    return this.get<IBank[]>(`/customer/business/${id}/banks`);
  }

  public static async getBusinessDeposits(id: number, params: CustomerDepositQuery) {
    return this.get<IPage<IDeposit>>(`/customer/business/${id}/deposits`, { params });
  }

  public static async getBusinessSalesReports(id: number, params: CustomerSalesReportQuery) {
    return this.get<IPage<BusinessSalesReport>>(`/customer/business/${id}/sales-reports`, { params });
  }
}
